import React from "react";
import { Box, Camera, GitMerge, Grid3X3, Lightbulb, MapPin, Spline } from "lucide-react";
import {
  ActuatorIcon,
  EllipsoidIcon,
  EntireObjectIcon,
  ExplosionIcon,
  InspectorIcon,
  JointIcon,
} from "../ui/Icons";
import { getConstraintDisplayName } from "../../utils/constraintNames";
import { getGeomInspectorDisplayLabel } from "../../utils/geomTypeEditing";
import { stripNamespacePrefix } from "../../utils/nameUtils";

const stripPrefix = stripNamespacePrefix;

export function resolveInspectorActivePanel({
  pendingVisualMeshParentBodyUuid,
  resolvedSelectedMeshVisualGeom,
  selectedBodies,
  selectedBody,
  selectedCamera,
  selectedEquality,
  selectedGeom,
  selectedGeomIsTendonWrap,
  selectedGeomIsVisual,
  selectedInertial,
  selectedJoint,
  selectedJointRecord,
  selectedLight,
  selectedMesh,
  selectedSite,
  selectedTendon,
  selectedVisualMeshCount,
  shouldShowObjectPanel,
}) {
  if (selectedEquality) return "equality";
  if (selectedTendon) return "tendon";
  if (selectedJoint && selectedJointRecord?.kind !== "free") return "joint";
  if (selectedCamera) return "camera";
  if (selectedLight) return "light";
  if (pendingVisualMeshParentBodyUuid) return "visualGeom";
  if (shouldShowObjectPanel) return "object";
  if (selectedVisualMeshCount > 1) return "multiMesh";
  if (selectedBodies.length > 1) return "multiBody";
  if (selectedBody) return "body";
  if (selectedGeom) {
    if (selectedGeomIsTendonWrap) return "tendonWrapGeom";
    return selectedGeomIsVisual ? "visualGeom" : "geom";
  }
  if (selectedInertial) return "inertial";
  if (selectedMesh && resolvedSelectedMeshVisualGeom) return "visualGeom";
  if (selectedSite) return "site";
  return null;
}

export function getInspectorPanelTitle({
  activePanel,
  allEqualities,
  allMeshes,
  pendingVisualMeshParentBodyUuid,
  resolvedActiveVisualGeom,
  resolvedSelectedGeom,
  selectedActuator,
  selectedActuatorRecord,
  selectedBodyName,
  selectedCamera,
  selectedCameraRecord,
  selectedEquality,
  selectedEqualityRecord,
  selectedInertialBodyName,
  selectedJoint,
  selectedJointRecord,
  selectedLight,
  selectedLightRecord,
  selectedSite,
  selectedSiteRecord,
  selectedTendon,
  selectedTendonRecord,
  selectedWorldBodyDisplayName,
}) {
  switch (activePanel) {
    case "camera": {
      const camera = selectedCamera ? selectedCameraRecord : null;
      return { name: stripPrefix(camera?.name) || "Camera", type: "camera" };
    }
    case "light": {
      const light = selectedLight ? selectedLightRecord : null;
      return { name: stripPrefix(light?.name) || "Light", type: "light" };
    }
    case "equality": {
      const equality = selectedEquality ? selectedEqualityRecord : null;
      return {
        name: selectedEquality
          ? getConstraintDisplayName(equality || selectedEquality, allEqualities)
          : "Constraint",
        type: "equality",
      };
    }
    case "tendon": {
      const tendon = selectedTendon ? selectedTendonRecord : null;
      return { name: stripPrefix(tendon?.name) || "Tendon", type: "tendon" };
    }
    case "actuator": {
      const actuator = selectedActuator ? selectedActuatorRecord : null;
      return { name: stripPrefix(actuator?.name) || "Actuator", type: "actuator" };
    }
    case "joint": {
      const joint = selectedJoint ? selectedJointRecord : null;
      return { name: stripPrefix(joint?.name) || "Joint", type: "joint" };
    }
    case "body":
      return { name: stripPrefix(selectedBodyName) || "Body", type: "body" };
    case "object":
      return {
        name: stripPrefix(selectedWorldBodyDisplayName || selectedBodyName) || "Object",
        type: "object",
      };
    case "multiBody":
      return { name: "Multiple Bodies", type: "body" };
    case "multiMesh":
      return { name: "Multiple Visuals", type: "mesh" };
    case "visualGeom": {
      if (pendingVisualMeshParentBodyUuid) {
        return { name: "New Visual Mesh", type: "visual" };
      }
      const geom = resolvedActiveVisualGeom?.geom || null;
      return {
        name: stripPrefix(getGeomInspectorDisplayLabel(geom, { meshes: allMeshes })) || "Visual",
        type: "visual",
      };
    }
    case "tendonWrapGeom": {
      const geom = resolvedSelectedGeom?.geom || null;
      return {
        name: stripPrefix(geom?.name || geom?.kind || geom?.type) || "Wrap Geom",
        type: "tendon wrap",
      };
    }
    case "site": {
      const site = selectedSite ? selectedSiteRecord : null;
      return { name: stripPrefix(site?.name || selectedSite) || "Site", type: "site" };
    }
    case "geom": {
      const geom = resolvedSelectedGeom?.geom || null;
      return { name: stripPrefix(geom?.name) || "Collision", type: "collision" };
    }
    case "inertial":
      return { name: stripPrefix(selectedInertialBodyName) || "Inertial", type: "inertial" };
    default:
      return null;
  }
}

export function InspectorPanelIcon({ activePanel, className = "w-5 h-5" }) {
  const iconClassName = `${className} text-gray-200 flex-shrink-0`;
  switch (activePanel) {
    case "camera":
      return <Camera className={iconClassName} />;
    case "light":
      return <Lightbulb className={iconClassName} />;
    case "equality":
      return <GitMerge className={iconClassName} />;
    case "tendon":
      return <Spline className={iconClassName} />;
    case "body":
      return <Box className={iconClassName} />;
    case "object":
      return <EntireObjectIcon className={iconClassName} />;
    case "visualGeom":
      return <Grid3X3 className={iconClassName} />;
    case "actuator":
      return <ActuatorIcon className={iconClassName} />;
    case "joint":
      return <JointIcon className={iconClassName} />;
    case "site":
      return <MapPin className={iconClassName} />;
    case "geom":
      return <ExplosionIcon className={iconClassName} />;
    case "inertial":
      return <EllipsoidIcon className={iconClassName} />;
    default:
      return <InspectorIcon className={iconClassName} />;
  }
}
